import {
  Component,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  ViewChildren,
  QueryList,
  AfterViewChecked,
} from "@angular/core";
import { BfxBlotterOrdersActionService } from "../bfx-blotter-orders/bfx-blotter-orders-action.service";
import { GoldenLayoutComponent } from "../golden-layout/golden-layout-component";
import { TranslateService } from "@ngx-translate/core";
import { GoldenLayoutPopout } from "../golden-layout/golden-layout-popout";
import { TabView } from "@bfx/primeng/tabview";
import { Subscription } from "rxjs";

import {
  SysFeatures,
  Roles,
  ActiveUser,
  OrderDataActionPromiseService,
  SystemConfigPromiseService,
  ActiveUserProfilePromiseService,
  PositionBookPromiseLookupService,
} from "shared/cubiq-services";

import { BfxDetailFillGridComponent } from "./bfx-detail-fill-grid.component";
import { FillOrdersModel } from "./models/FillOrdersModel";
import { BfxDetailFillService } from "./services/bfx-detail-fill.service";

export const MIN_VISIBLE_ROWS: number = 7;

const COLUMN_WEIGHTS: Array<number> = [45, 20, 15, 20];

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import { DETAIL_FILL_TABLE_DEFINITION_VER } from "shared/cubiq-services/model/TableDefinitionFactory.constants";
import { FillOrderGroup } from "./models/FillOrderGroup";
import { OrderIntent } from "shared/cubiq-model-multi-asset";

@Component({
  selector: "bfx-detail-fill",
  templateUrl: "./html/bfx-detail-fill.html",
  standalone: false,
})
export class BfxDetailFillComponent
  implements GoldenLayoutComponent, OnDestroy, AfterViewInit, AfterViewChecked
{
  public fillOrdersModel: FillOrdersModel;
  public vwap: string;
  public showClose: boolean = true;
  public loading: boolean = true;

  public scrollWidth: string = "750px";
  public scrollHeight: string = "180px";

  public message: string = "";
  public parentPopout: GoldenLayoutPopout = null;
  public progress: number = 0;
  public modelEmptiedSubscription: Subscription;
  public freezeButtonUpdates: boolean = false;

  public canFill: boolean = false;

  public activeUser: ActiveUser = null;

  public columnsConfiguration: any = {};
  public sortingModel: any = null;
  public blotterId: string;

  public updateLayoutCallback: any;
  public persistEmbeddedComponentConfigData: boolean = true;

  public curPosBook: any = null;
  public allPosBooks: Array<any> = [];
  public suggestionsPosBooks: Array<any> = [];

  public curSlippage: string = null;

  public positionBookSelectionDisabled: boolean = false;

  private selectedTabIdx: number = 0;
  private selectedTabName: string = null;
  private tableElement: any;
  private scrollTableElement: any;
  private allGridsSubscription: Subscription;
  private dataChangeSubscription: Subscription;

  @ViewChildren("gridTabs") allGrids: QueryList<BfxDetailFillGridComponent>;
  @ViewChild("tabView", { static: true }) tabComponent: TabView;

  public intentType: OrderIntent;
  public OI = OrderIntent;

  public control: {
    mode: boolean;
    containerHeight: number;
    scrollbarConfig: {
      suppressScrollX: boolean;
    };
    positionBookAutocompleteConfig: {
      allowEmpty: boolean;
      forceSelection: boolean;
      customValidation: any;
    };
  } = {
    mode: true,
    containerHeight: 324,
    scrollbarConfig: {
      suppressScrollX: true,
    },
    positionBookAutocompleteConfig: {
      allowEmpty: true,
      forceSelection: true,
      customValidation: null,
    },
  };

  constructor(
    public orderActionService: OrderDataActionPromiseService,
    public activeUserService: ActiveUserProfilePromiseService,
    public blotterOrderActionService: BfxBlotterOrdersActionService,
    public systemConfigService: SystemConfigPromiseService,
    public element: ElementRef,
    public translationService: TranslateService,
    public detailFillService: BfxDetailFillService,
    private posBookService: PositionBookPromiseLookupService,
  ) {
    this.initialize();
  }

  pairChange(base: string, nonBase: string): void {
    if (!this.updatePositionBooks(base, nonBase)) {
      this.posBookService.getPositionBooksForPairAndUserBranch(base, nonBase).then((values) => {
        if (values) {
          this.allPosBooks = values.concat();
        } else {
          this.allPosBooks = [];
        }
      });
    }
  }

  protected initialize(): void {
    this.systemConfigService.hasRole(Roles.ORDER_FILL).then((value: boolean) => {
      this.fillOrdersModel.canFill = value;
    });

    this.activeUserService.getActiveUserData().then((data: ActiveUser) => {
      this.activeUser = data;
    });

    // We bring the window up if in native desktop
    this.dataChangeSubscription = this.detailFillService.dataUpdatedSubject.subscribe((val) => {
      if (this.parentPopout != null) {
        try {
          this.parentPopout.bringToFront();
        } catch (e) {
          logger.out.warn(
            `${this.constructor.name}: Failed to bring detail` + ` fill to focus: ${e}`,
            e,
          );
        }

        try {
          setTimeout(() => {
            if (Array.isArray(val) && val.length > 0) {
              let pair = val[0].pairName;

              let group = this.fillOrdersModel.groups.find((g) => g.pairName === pair);
              if (group != null) {
                let index = this.fillOrdersModel.groups.indexOf(group);
                let tabPanels = this.tabComponent.tabPanels;
                if (index >= 0 && tabPanels.length > index) {
                  this.tabComponent.tabPanels.forEach((p, i) => {
                    p.selected = i === index;
                  });
                  this.handleTabChange({ index: index });
                }
              }
            }
          }, 0);
        } catch (e) {}
      }
    });
  }

  ngAfterViewInit() {
    this.allGridsSubscription = this.allGrids.changes.subscribe(this.alignGrids.bind(this));

    this.fillOrdersModel.ownerDocument =
      this.element != null && this.element.nativeElement.ownerDocument
        ? this.element.nativeElement.ownerDocument
        : document;
  }

  ngOnDestroy() {
    if (this.modelEmptiedSubscription) {
      this.modelEmptiedSubscription.unsubscribe();
    }

    if (this.allGridsSubscription) {
      this.allGridsSubscription.unsubscribe();
    }

    if (this.dataChangeSubscription) {
      this.dataChangeSubscription.unsubscribe();
    }

    this.closeAll();
  }

  glInit(data: any, container: any, state: any): void {
    this.fillOrdersModel = this.detailFillService.getFillOrdersModel();

    this.modelEmptiedSubscription = this.fillOrdersModel.modelEmptiedSubject.subscribe(
      this.closeIfEmpty.bind(this),
    );
    if (
      this.fillOrdersModel &&
      this.fillOrdersModel.groups &&
      this.fillOrdersModel.groups.length > 0
    ) {
      this.selectedTabName = this.fillOrdersModel.groups[0].pairName;
      this.pairChange(
        this.fillOrdersModel.groups[0].getBase(),
        this.fillOrdersModel.groups[0].getNonBase(),
      );
      if (
        this.curSlippage == null ||
        (this.curSlippage == "" && this.fillOrdersModel.groups[0].slippage != null)
      ) {
        this.curSlippage = this.fillOrdersModel.groups[0].slippage;
      }
    }
  }

  setParentPopup(parentPopup: GoldenLayoutPopout) {
    let columnConfigurationData: any = {};
    let sortingModelData: any = {};
    this.parentPopout = parentPopup;
    this.parentPopout.setShowDock(false);

    let { detailFillBlotterConfig } = this.parentPopout.embeddedComponentConfig.data;
    if (detailFillBlotterConfig != null) {
      let { columnsConfiguration, sortingModel } = detailFillBlotterConfig;
      columnConfigurationData = columnsConfiguration;
      sortingModelData = sortingModel;
      this.restoreFromConfiguration(detailFillBlotterConfig);
    }

    this.columnsConfiguration = columnConfigurationData;
    this.sortingModel = sortingModelData;
    this.alignGrids();
  }

  /**
   * Callback that determines if this popup should be closed or not
   */
  public onClose(): boolean {
    return true;
  }

  /**
   *
   */
  // protected openConfirmDialog(payload: any): void {
  //     let bfxDialogData: BfxDialogData = new BfxDialogData(BfxDialogCommand.DIALOG_ORDER_CANCEL_CONFIRM_OPEN, null, null, payload);
  //     this.blotterOrderActionService.launchNewDialog(bfxDialogData);
  // }

  onTabClose($event): void {
    let tabIdx = $event.index;
    let ids = this.fillOrdersModel.getGroupIds(tabIdx);
    if (ids.length > 0) {
      this.detailFillService.unlockOrders(ids);
    }
  }
  closeAll(): void {
    let ids = this.fillOrdersModel.getAllTabIdsToUnlock();
    if (ids.length > 0) {
      this.detailFillService.unlockOrders(ids);
    }
  }
  close(): void {
    let ids = this.fillOrdersModel.getAllIds();
    if (ids.length > 0) {
      this.detailFillService.unlockOrders(ids);
    }
  }

  selectedOrdersChange(event) {
    this.fillOrdersModel.updateButtonFlags();
    this.showVwap();
    this.updatePositionBooks();
  }

  gridLayoutChange(event) {
    if (this.allGrids.first) {
      this.updateConfig(this.allGrids.first);
    }
  }

  handleTabChange(event) {
    this.selectedTabIdx = event.index;
    this.showVwap();
    this.detailFillService.tabIndexChange(this.selectedTabIdx);
    let curGrid = this.allGrids.find((grid: BfxDetailFillGridComponent, index: number) => {
      return index == this.selectedTabIdx;
    });
    if (curGrid) {
      let curName = this.fillOrdersModel.groups[this.selectedTabIdx].pairName;
      if (curName != this.selectedTabName) {
        this.selectedTabName = curName;
        this.pairChange(
          this.fillOrdersModel.groups[this.selectedTabIdx].getBase(),
          this.fillOrdersModel.groups[this.selectedTabIdx].getNonBase(),
        );
      }
      this.curSlippage = this.fillOrdersModel.groups[this.selectedTabIdx].slippage;
      this.curPosBook = this.fillOrdersModel.groups[this.selectedTabIdx].posBook;
      setTimeout(() => {
        curGrid.gridOptions.api.doLayout();
      }, 50);
    }
  }

  showVwap(): void {
    if (this.fillOrdersModel.groups[this.selectedTabIdx]) {
      this.vwap = this.fillOrdersModel.groups[this.selectedTabIdx].vwap;
      this.intentType = this.fillOrdersModel.groups[this.selectedTabIdx].vwapIntent;
    }
  }

  reactivate(): void {
    let triggeredIds: Array<string> = this.fillOrdersModel.getSelectedTriggeredIds();
    if (triggeredIds.length > 0) this.orderActionService.reactivateOrders(triggeredIds, true);
  }

  closeIfEmpty(): void {
    if (this.fillOrdersModel && this.fillOrdersModel.getTotalCount() == 0) {
      this.parentPopout.close();
    }
  }

  ngAfterViewChecked(): void {}

  alignGrids(): void {
    if (this.allGrids) {
      this.allGrids.forEach((grid) => {
        let otherGrids = this.allGrids.filter((egrid) => egrid != grid);
        grid.gridOptions.alignedGrids.splice(0, grid.gridOptions.alignedGrids.length);
        otherGrids.forEach((other) => {
          grid.gridOptions.alignedGrids.push(other.gridOptions);
        });
      });
      if (this.tabComponent.tabs) {
        let curIndex: number = -1;
        this.tabComponent.tabs.forEach((tab) => {
          curIndex++;
          if (tab.selected) {
            let curName = this.fillOrdersModel.groups[curIndex].pairName;
            if (curName != this.selectedTabName) {
              this.handleTabChange({ index: curIndex });
            } else {
              // might be on the same tab, but the index may have changed
              this.selectedTabIdx = curIndex;
              this.detailFillService.tabIndexChange(this.selectedTabIdx);
            }
            return;
          }
        });
      }
    }
  }

  public fillSelected() {
    this.detailFillService.fillSelected(this.element.nativeElement);
  }

  public getComponentConfiguration(): any {
    return {
      blotterId: this.blotterId,
      columnsConfiguration: this.columnsConfiguration,
      sortingModel: this.sortingModel,
      version: DETAIL_FILL_TABLE_DEFINITION_VER,
    };
  }

  private updateConfig(grid: BfxDetailFillGridComponent): void {
    this.blotterId = grid.blotterId;
    this.columnsConfiguration = grid.getBlotterColumnsConfigs();
    this.sortingModel = grid.getBlotterSortingModel();
    if (this.updateLayoutCallback) {
      this.parentPopout.embeddedComponentConfig.data["detailFillBlotterConfig"] =
        this.getComponentConfiguration();
      this.updateLayoutCallback();
    }
  }

  public setLayoutUpdatedCallback?(callBack: any): void {
    this.updateLayoutCallback = callBack;
  }

  /** NOTE: Initial invocation from golden layout does not contain column data since
   * pop-out configs components get saved in the AdditionalProfileConfigurations.
   * The config data is set in the embeddedComponentConfig in the parentPopout,
   * so the call is made when the parentPopout component is available.
   */
  public restoreFromConfiguration(config: any): void {
    if (config != null && config.columnsConfiguration && config.sortingModel) {
      let { columnsConfiguration, sortingModel } = config;
      this.columnsConfiguration = columnsConfiguration;
      this.sortingModel = sortingModel;

      this.allGrids?.forEach((c) => c.restoreFromConfiguration(config));
    }
  }

  public searchPosBooks(filter: any): void {
    let query: string = filter.query.toLowerCase();
    if (query.length > 0) {
      this.suggestionsPosBooks = this.allPosBooks.filter((posbook: any) =>
        posbook.label.toLowerCase().includes(query),
      );
    } else {
      this.suggestionsPosBooks = this.allPosBooks.slice(0);
    }
  }
  public onPosBookFocus(event): void {
    this.clearGridCellSelection();
  }

  private clearGridCellSelection(): void {
    let curGrid = this.allGrids.find((grid: BfxDetailFillGridComponent, index: number) => {
      return index == this.selectedTabIdx;
    });
    if (curGrid) {
      curGrid.clearGridCellSelection();
    }
  }

  public onSelectPosBook(data: any): void {
    if (data) {
      this.fillOrdersModel.setPositionBook(data);
    }
  }

  public undocked(): void {
    if (NativeDesktopSupport.isNativeDesktopContainer()) {
      let ownerDocument: Document = document;

      if (this.element != null && this.element.nativeElement.ownerDocument != null) {
        ownerDocument = this.element.nativeElement.ownerDocument;
      }

      this.fillOrdersModel.ownerDocument = ownerDocument;
    }
  }

  /**
   * Update the position books selection options for the global dropdown.
   *
   * @param base optional base currency, if not provided its read from the selected group
   * @param nonBase optional non base currency, if not provided its read from the selected group
   * @returns true if the options were updated, false if this was skipped
   */
  protected updatePositionBooks(base: string = null, nonBase: string = null): boolean {
    if (
      this.fillOrdersModel != null &&
      Array.isArray(this.fillOrdersModel.groups) &&
      this.selectedTabIdx >= 0 &&
      this.selectedTabIdx < this.fillOrdersModel.groups.length
    ) {
      let fillOrderGroup: FillOrderGroup = this.fillOrdersModel.groups[this.selectedTabIdx];

      base = base == null ? fillOrderGroup.getBase() : base;
      nonBase = nonBase == null ? fillOrderGroup.getNonBase() : nonBase;

      let traderGroupBranch: string = fillOrderGroup.getOrderTraderGroupBranch();
      this.positionBookSelectionDisabled = true;

      if (traderGroupBranch != null) {
        this.posBookService
          .getPositionBooksForPairAndBranch(base, nonBase, traderGroupBranch)
          .then((values) => {
            if (values) {
              this.allPosBooks = values.concat();
            } else {
              this.allPosBooks = [];
            }
            this.positionBookSelectionDisabled = false;
          });
      } else {
        this.allPosBooks = [];
        this.curPosBook = null;
      }

      return true;
    }

    return false;
  }
}
